import { createRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { createAuthToken } from '@/api/internal-token-client'
import { AdyenDropin } from '@/components/ui/adyen'
import { Button } from '@/components/ui/button'
import { SessionType } from '@/lib/enums'
import { env } from '@/lib/env'
import { postToHost, SUBMIT_LABEL_TOKENIZATION } from '@/lib/post-message'

import { Route as rootRoute } from './__root'

import type { AdyenDropinHandle } from '@/components/ui/adyen'
import type { ErrorWithState } from '@/lib/types'

const searchSchema = z.object({
  sessionData: z.string().optional(),
})

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tokenization-session',
  validateSearch: (search) => searchSchema.parse(search),
  component: TokenizationSessionPage,
})

function TokenizationSessionPage() {
  const { sessionData } = Route.useSearch()
  if (!sessionData) return <IdleState />
  return <TokenizationSessionForm sessionData={sessionData} />
}

function IdleState() {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <p className="text-foreground text-sm">Invalid URL parameters.</p>
    </div>
  )
}

function TokenizationSessionForm({ sessionData }: { sessionData: string }) {
  const [token, setToken] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState<Error | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const dropinRef = useRef<AdyenDropinHandle>(null)

  useEffect(() => {
    let cancelled = false
    createAuthToken(env.INTERNAL_API_HOST, sessionData)
      .then((response) => {
        if (!cancelled) setToken(response.token)
      })
      .catch((error: Error) => {
        if (!cancelled) setTokenError(error)
      })
    return () => {
      cancelled = true
    }
  }, [sessionData])

  const handleReady = () => {
    postToHost('card-payment-setup')
  }

  const handleSubmit = async () => {
    if (!dropinRef.current) return
    setSubmitting(true)
    try {
      const result = await dropinRef.current.submit()
      // For tokenization, the success path also emits card-tokenization-completed
      // from inside the Adyen callback (see specs/app/index.md::Decisions step 12)
      // — that's already fired by the time submit() returns here. This emit is
      // the additional card-payment-completed (step 10b) preserving the
      // documented dual-message tokenization happy path.
      if (result.refusal) {
        postToHost('card-payment-failed')
      } else {
        postToHost('card-payment-completed')
        // AdyenDropin hides itself (form + Button children) on status COMPLETED.
      }
    } catch (error) {
      const e = error as ErrorWithState
      if (e?.state?.isValid) {
        postToHost('card-payment-error')
      } else {
        postToHost('card-payment-form-invalid')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (tokenError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-destructive text-sm">Failed to initialize session.</p>
      </div>
    )
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-muted-foreground text-sm">Loading…</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-start gap-4 p-6">
      <AdyenDropin
        ref={dropinRef}
        internalApiHost={env.INTERNAL_API_HOST}
        authToken={token}
        adyenClientKey={env.ADYEN_CLIENT_KEY}
        adyenEnvironment={env.ENV === 'production' ? 'live' : 'test'}
        sessionType={SessionType.TOKENIZATION}
        encodedSessionData={sessionData}
        showCardholderName={Boolean(env.WEB_DROP_IN_SHOW_NAME)}
        onReady={handleReady}
        className="w-full max-w-md"
      >
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="adyen-checkout__button adyen-checkout__button--pay"
        >
          {SUBMIT_LABEL_TOKENIZATION}
        </Button>
      </AdyenDropin>
    </div>
  )
}
